import { Component, HostListener, OnInit } from '@angular/core';
import { RelatorioVendasParams } from '../../../models/relatorios/relatorio-vendas.model';
import { RelatorioVendasService } from '../../../services/relatorios/relatorio-vendas.service';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { TimbradoService } from '../../../services/configuracoes/timbrado.service';
import { PessoaDTO } from '../../../models/clientes/pessoa.model';
import { PeriodoFiltro } from '../../../models/dashboard/periodo-filtro.model';
import { NotaService } from '../../../services/financeiro/notas.service';
import { NotaDTO } from '../../../models/nota.model';
import { TimbradoDTO } from '../../../models/configuracoes/timbrao.model';
import { FileUtils } from '../../../shared/utils/file-utils';
import { calcularPeriodoPadraoRelatorio } from '../../../shared/utils/periodo-relatorio-padrao.util';
import { PRESETS_PERIODO_RELATORIO } from '../../../shared/utils/periodo.utils';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-relatorio-vendas',
  templateUrl: './relatorio-vendas.component.html',
  styleUrls: ['./relatorio-vendas.component.scss']
})
export class RelatorioVendasComponent implements OnInit {

  // Texto exibido no botao do filtro de periodo (app-periodo-filtro, modo avancado).
  periodoTexto = '';

  // Presets do filtro de periodo: lista padrao + "Mostrar todos" (so relatorios).
  readonly opcoesPeriodo = PRESETS_PERIODO_RELATORIO;

  params: RelatorioVendasParams = {
    dataHoraInicial: '',
    dataHoraFinal: '',
    pessoaId: undefined,
    vendedorId: undefined,
    notaId: undefined,
    timbradoId: undefined,
    ehFaturaLegal: undefined,
    cancelado: undefined,
    exibirMercadorias: false,
    exibirMercadoriasResumida: false,
    exibirFormaDePagamentoResumida: false,
    exibirFormaPagamentoDetalhada: false,
  };

  clientes: any[] = [];
  selectedCliente: PessoaDTO | undefined;
  searchNome: string = '';
  totalRecords: number = 0;
  first3: number = 0;
  loadingClientes: boolean = false;

  // Filtro de vendedor (Pessoa com categoriaVendedor=true).
  // Usa variavel de busca dedicada para nao interferir na digitacao dos outros dropdowns.
  vendedores: any[] = [];
  selectedVendedor: PessoaDTO | undefined;
  searchNomeVendedor: string = '';
  loadingVendedores: boolean = false;

  notas: any[] = [];
  selectedNota: NotaDTO | undefined;
  loadingNotas: boolean = false;

  timbrados: any[] = [];
  selectedTimbrado: TimbradoDTO | undefined;
  loadingTimbrados: boolean = false;

  emitindoRelatorio: boolean = false;

  constructor(
    private translate: TranslateService,
    private relatorioVendasService: RelatorioVendasService,
    private messageService: MessageService,
    private pessoaService: PessoaService,
    private timbradoService: TimbradoService,
    private notaService: NotaService,
  ) { }

  ngOnInit(): void {
    this.inicializarPeriodoPadrao();
    this.findClientes();
    this.findVendedores();
    this.findNotas();
    this.findTimbrados();
    this.selectedTimbrado = this.timbradosDeVenda[0];
  }

  // Inicializa o periodo padrao (Este mes) para que os params tenham datas
  // antes de o usuario abrir o dialog do filtro. O app-periodo-filtro nao
  // emite no init, entao replicamos o que o dashboard faz.
  private inicializarPeriodoPadrao(): void {
    this.aplicarPeriodo(calcularPeriodoPadraoRelatorio(this.translate));
  }

  // Handler do (periodoChanged) do app-periodo-filtro.
  onPeriodoChanged(periodo: PeriodoFiltro): void {
    this.aplicarPeriodo(periodo);
  }

  private aplicarPeriodo(periodo: PeriodoFiltro): void {
    this.params.dataHoraInicial = periodo.dataInicio;
    this.params.dataHoraFinal = periodo.dataFim;
    this.periodoTexto = periodo.texto;
  }

  emitirRelatorio(): void {
    this.emitindoRelatorio = true;

    // As datas ja foram aplicadas em params via onPeriodoChanged / periodo padrao.
    this.params.pessoaId = this.selectedCliente ? this.selectedCliente.id : undefined;
    this.params.vendedorId = this.selectedVendedor ? this.selectedVendedor.id : undefined;
    this.params.timbradoId = this.selectedTimbrado ? this.selectedTimbrado.id : undefined;
    this.params.notaId = this.selectedNota ? this.selectedNota.id : undefined;

    this.relatorioVendasService.emitirRelatorioVendas(this.params)
      .subscribe({
        next: (base64Pdf: string) => {
          FileUtils.abrirPdf(base64Pdf, 'relatorio-vendas.pdf');
          this.emitindoRelatorio = false;
        },
        error: (err) => {
          console.error('Erro ao gerar relatório', err);
          this.emitindoRelatorio = false;
          if (err?.status === 404) {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('common.aviso'),
              detail: this.translate.instant('relatorios.mensagens.nenhum_dado_encontrado'),
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.erro'),
              detail: this.translate.instant('relatorios.mensagens.erro_gerar_relatorio'),
            });
          }
        }
      });
  }

  findClientes(page: number = 0, sort: string = 'id,asc') {
    const size = 10;
    let filter = '';

    filter += `&email=${this.searchNome}`;
    filter += `&nome=${this.searchNome}`;
    filter += `&fantasia=${this.searchNome}`;
    filter += `&ruc=${this.searchNome}`;
    filter += `&telefone=${this.searchNome}`;
    filter += `&ci=${this.searchNome}`;
    filter += `&nomeCidade=${this.searchNome}`;

    if (!isNaN(Number(this.searchNome))) {
      filter += `&codigoPessoa=${this.searchNome}`;
    }
    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter: `categoriaCliente=true${filter ? `&${filter}` : ''}`,
      })
      .subscribe(
        (response: any) => {
          this.clientes = response.content;
          this.totalRecords = response.totalElements;
          this.first3 = page * size;
        },
        (error: any) => {
          console.error('Erro ao buscar clientes:', error);
          this.clientes = [];
          this.totalRecords = 0;
        }
      );
  }

  onClientesFilter(event: any) {
    this.searchNome = event.filter?.trim() || '';
    this.loadingClientes = true;
    this.findClientes();
    this.loadingClientes = false;
  }

  findVendedores(page: number = 0, sort: string = 'id,asc') {
    const size = 10;
    let filter = '';

    filter += `&email=${this.searchNomeVendedor}`;
    filter += `&nome=${this.searchNomeVendedor}`;
    filter += `&fantasia=${this.searchNomeVendedor}`;
    filter += `&ruc=${this.searchNomeVendedor}`;
    filter += `&telefone=${this.searchNomeVendedor}`;
    filter += `&ci=${this.searchNomeVendedor}`;
    filter += `&nomeCidade=${this.searchNomeVendedor}`;

    if (!isNaN(Number(this.searchNomeVendedor))) {
      filter += `&codigoPessoa=${this.searchNomeVendedor}`;
    }
    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter: `categoriaVendedor=true${filter ? `&${filter}` : ''}`,
      })
      .subscribe(
        (response: any) => {
          this.vendedores = response.content;
        },
        (error: any) => {
          console.error('Erro ao buscar vendedores:', error);
          this.vendedores = [];
        }
      );
  }

  onVendedoresFilter(event: any) {
    this.searchNomeVendedor = event.filter?.trim() || '';
    this.loadingVendedores = true;
    this.findVendedores();
    this.loadingVendedores = false;
  }

  findNotas(page: number = 0, sort: string = 'id,asc') {
    const size = 10;
    let filter = '';
    filter += `codigoNota=${this.searchNome}`;

    this.notaService.get(
      '/nota/buscar', {
      page,
      size,
      sort,
      filter: `tipoDeTransacao=VENDA${filter ? `&${filter}` : ''}`
    }).subscribe({
      next: (res) => {
        this.notas = res.content;
      },
      error: (err) => console.error(err),
    });
  }

  onNotasFilter(event: any) {
    this.searchNome = event.filter?.trim() || '';
    this.loadingNotas = true;
    this.findNotas();
    this.loadingNotas = false;
  }

  findTimbrados(page: number = 0, sort: string = 'id,asc') {
    this.timbradoService.get('/timbrado').subscribe({
      next: (res) => {
        this.timbrados = res.content;
      },
      error: (err) => console.error(err)
    });
  }

  onTimbradosFilter(event: any) {
    this.searchNome = event.filter?.trim() || '';
    this.loadingTimbrados = true;
    this.findTimbrados();
    this.loadingTimbrados = false;
  }

  get timbradosDeVenda() {
    const timbradosFiltrados = this.timbrados.filter(
      t => t.tipoDeTransacao?.enum === 'VENDA'
    );

    // Adiciona a opção "Venda Común" manualmente no início da lista caso não exista
    if (!timbradosFiltrados.some(t => t.tipoNota?.nome === 'Venta Común')) {
      timbradosFiltrados.unshift({
        tipoNota: { nome: 'Venta Común' }
      });
    }
    return timbradosFiltrados;
  }

  // Métodos para selecionar cliente e timbrado permanecem iguais.
  onClienteSelecionado(clienteId: number): void {
    this.params.pessoaId = clienteId;
  }

  onTimbradoSelecionado(timbradoId: number): void {
    this.params.timbradoId = timbradoId;
  }

  get emissaoNotaFormatada(): string {
    return this.formatarDataEHora(new Date().toISOString());
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  private formatarNumeroBaseFatura(numeroFilial?: number, pontoDeExpedicao?: number, numeroFatura?: number): string {
    if (numeroFilial == null || pontoDeExpedicao == null || numeroFatura == null) {
      return '';
    }

    const filial = String(numeroFilial).padStart(3, '0');
    const expedicao = String(pontoDeExpedicao).padStart(3, '0');
    const fatura = String(numeroFatura).padStart(7, '0');

    return `${filial}-${expedicao}-${fatura}`;
  }

  formatarNumeroTimbrado(timbrado?: TimbradoDTO): string {
    if (!timbrado) return '';
    return this.formatarNumeroBaseFatura(
      timbrado.numeroFilial,
      timbrado.pontoDeExpedicao,
      timbrado.proximoNumero
    );
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.key === 'Enter' && !this.emitindoRelatorio) {
      event.preventDefault();
      this.emitirRelatorio();
    }
  }
}
